import {
  BINARY_DEMOGRAPHIC_GROUP_IDS,
  DEMOGRAPHIC_GROUP_IDS,
  DEMOGRAPHIC_IDS,
} from "../../types/report";
import {
  Demographic,
  DemographicGroupWithDetails,
} from "../../types/workplaceCulture";

export const SCORE_THRESHOLD = 2.5;
export const BOTTOM_N_COUNT = 3;
export const BOTTOM_N_RANGE_THRESHOLD = 0.25;
export const BINARY_GROUP_SCORE_RANGE_THRESHOLD = 0.75;

export const getChapterScoresForDemographicGroup = (
  demographicGroup: DemographicGroupWithDetails,
  chapterId: string,
  includeIsSkip = false
): number[] => {
  const filteredDemographics = includeIsSkip
    ? demographicGroup.demographics
    : demographicGroup.demographics.filter((d) => !d.is_skip);
  return filteredDemographics
    .map((demographic) => {
      const chapterScore = demographic.chapter_scores.find(
        (cs) => cs.id === chapterId
      );
      return chapterScore?.score;
    })
    .filter((score): score is number => score !== null && score !== undefined);
};

const getAllDemographicsWithScores = (
  demographicGroups: DemographicGroupWithDetails[],
  chapterId: string
): Array<{ demographic: Demographic; chapterScore: number }> => {
  // Collect all demographics with valid chapter-specific scores
  const allDemographicsWithScores: Array<{
    demographic: Demographic;
    chapterScore: number;
  }> = [];

  demographicGroups.forEach((group) => {
    group.demographics.forEach((demographic: Demographic) => {
      // Find the score for this specific chapter
      const chapterScore = demographic.chapter_scores.find(
        (cs) => cs.id === chapterId
      );

      if (
        chapterScore &&
        chapterScore.score !== null &&
        chapterScore.score !== undefined &&
        !demographic.is_skip
      ) {
        allDemographicsWithScores.push({
          demographic,
          chapterScore: chapterScore.score,
        });
      }
    });
  });

  return allDemographicsWithScores;
};

const getBottomNChapterScores = (
  demographicGroups: DemographicGroupWithDetails[],
  chapterId: string,
  n: number
): Demographic[] => {
  // Collect all demographics with valid chapter-specific scores
  const allDemographicsWithScores = getAllDemographicsWithScores(
    demographicGroups,
    chapterId
  );

  // Sort by chapter score (ascending) to find bottom N
  const sortedDemographics = [...allDemographicsWithScores].sort(
    (a, b) => a.chapterScore - b.chapterScore
  );

  // Calculate the range of scores in this chapter
  const bottomNDemographics = sortedDemographics.slice(0, BOTTOM_N_COUNT);

  return bottomNDemographics.map(({ demographic }) => demographic);
};

export const getSpecialAttentionDemographics = (
  demographicGroups: DemographicGroupWithDetails[],
  chapterId: string
): Demographic[] => {
  // Filter out the Organisation demographic group
  const filteredGroups = demographicGroups.filter(
    (group) => group.id !== DEMOGRAPHIC_GROUP_IDS.ORGANISATION
  );

  // Combine demographics that meet any of these criteria:
  // 1. In bottom N chapter scores (only if range > 0.25)
  // 2. Chapter score below threshold
  // 3. In a binary group with >0.75 range and has the lower score
  const specialAttentionSet = new Set<string>();
  const result: Demographic[] = [];

  // 1. In bottom N chapter scores (only if range > 0.25)
  // Only apply this criterion if there are at least N+1 demographics total
  const allDemographicsCount = getAllDemographicsWithScores(
    filteredGroups,
    chapterId
  ).length;

  if (allDemographicsCount > BOTTOM_N_COUNT) {
    // Get the bottom N demographics based on chapter scores
    const bottomNDemographics = getBottomNChapterScores(
      filteredGroups,
      chapterId,
      BOTTOM_N_COUNT
    );

    // Filter out demographics that don't meet the range threshold
    bottomNDemographics.forEach((demographic) => {
      const demographicGroup = filteredGroups.find((group) =>
        group.demographics.some((d) => d.id === demographic.id)
      );

      if (!demographicGroup) return;

      // find the range within this demographic group
      const scores = getChapterScoresForDemographicGroup(
        demographicGroup,
        chapterId
      );
      const minScore = scores.length > 0 ? Math.min(...scores) : 0;
      const maxScore = scores.length > 0 ? Math.max(...scores) : 0;
      const scoreRange = maxScore - minScore;

      if (scoreRange > BOTTOM_N_RANGE_THRESHOLD) {
        specialAttentionSet.add(demographic.id);
        result.push(demographic);
      }
    });
  }

  // 2. Chapter score below threshold
  const allDemographicsWithScores = getAllDemographicsWithScores(
    filteredGroups,
    chapterId
  );
  allDemographicsWithScores.forEach(({ demographic, chapterScore }) => {
    if (chapterScore <= SCORE_THRESHOLD) {
      if (!specialAttentionSet.has(demographic.id)) {
        specialAttentionSet.add(demographic.id);
        result.push(demographic);
      }
    }
  });

  // 3. In a binary group with >0.75 range and has the lower score
  filteredGroups.forEach((group) => {
    // Check if this is a binary demographic group
    if (BINARY_DEMOGRAPHIC_GROUP_IDS.includes(group.id as any)) {
      // Get valid demographics with chapter scores
      let validDemographics = getAllDemographicsWithScores([group], chapterId);

      if (
        group.id === DEMOGRAPHIC_GROUP_IDS.GENDER ||
        group.id === DEMOGRAPHIC_GROUP_IDS.LGBTQ
      ) {
        validDemographics = validDemographics.filter(
          ({ demographic }) =>
            demographic.id ===
              DEMOGRAPHIC_IDS.TRANSGENDER_FROM_TRANSGENDER_GROUP ||
            demographic.id === DEMOGRAPHIC_IDS.TRANSGENDER_FROM_LGBTQ_GROUP
        );
      }

      if (validDemographics.length >= 2) {
        // Find min and max chapter scores
        const scores = validDemographics.map((d) => d.chapterScore as number);
        const minScore = Math.min(...scores);
        const maxScore = Math.max(...scores);
        const range = maxScore - minScore;

        // If range > 0.75, add demographic(s) with the lowest score
        if (range > BINARY_GROUP_SCORE_RANGE_THRESHOLD) {
          validDemographics.forEach(({ demographic, chapterScore }) => {
            if (chapterScore === minScore) {
              if (!specialAttentionSet.has(demographic.id)) {
                specialAttentionSet.add(demographic.id);
                result.push(demographic);
              }
            }
          });
        }
      }
    }
  });

  return result;
};
